import React from 'react';
import { motion } from 'framer-motion';
import { Bolt, Check, ArrowUpRight, Shield, Zap, Database } from 'lucide-react';

const Slide10Roadmap = ({ isPreview = false }) => {
  const moatPoints = [
    {
      icon: <Zap className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />,
      title: "Execution, Not Just Viewing",
      desc: "Competitors like IndMoney show you the data. ADI logs, Analyzes and Suggests the fixes for the problem."
    },
    {
      icon: <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />,
      title: "India-Native Tax Intelligence",
      desc: "Proprietary models built on the 2025 Indian Tax Codes, not generic US-based algos."
    },
    {
      icon: <Database className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />,
      title: "Deep AA Integration",
      desc: "Leveraging Account Aggregator framework for consented, real-time read access. For Better Analytics."
    }
  ];

  return (
    <div className="w-full h-full min-h-[100svh] lg:min-h-0 bg-background flex flex-col p-4 sm:p-8 lg:p-16 overflow-hidden relative">
      {/* Background Grid */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{ backgroundImage: 'linear-gradient(white 1px, transparent 1px), linear-gradient(90deg, white 1px, transparent 1px)', backgroundSize: '40px 40px' }}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end mb-6 sm:mb-8 lg:mb-12 relative z-10 gap-4">
        <div>
          <div className="flex items-center gap-2 sm:gap-3 mb-2">
            <span className="text-primary text-xs sm:text-sm font-mono uppercase tracking-[0.2em] border border-primary/20 px-2 sm:px-3 py-1 rounded bg-black/50">Competitive Landscape</span>
            <div className="w-8 sm:w-16 h-px bg-primary/20"></div>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tight text-foreground leading-tight">
            The Autonomy <span className="text-muted-foreground">Advantage</span>
          </h1>
        </div>
        <div className="text-left sm:text-right">
          <p className="font-mono text-xs sm:text-sm text-muted-foreground uppercase tracking-widest mb-1">Differentiation Strategy</p>
          <p className="font-mono text-primary text-sm sm:text-base font-bold">Action &gt; Analysis</p>
        </div>
      </div>

      <div className="flex-1 flex flex-col lg:grid lg:grid-cols-12 gap-6 sm:gap-8 lg:gap-12 h-full pb-4 sm:pb-8 relative z-10 overflow-y-auto">

        {/* Left: The Matrix (7 Cols on desktop) */}
        <motion.div
          initial={isPreview ? {} : { opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-7 relative bg-card/50 border border-white/10 rounded-xl sm:rounded-2xl lg:rounded-3xl p-4 sm:p-6 lg:p-8 flex items-center justify-center shadow-2xl overflow-hidden min-h-[300px] sm:min-h-[400px]"
        >
          {/* Axis Lines */}
          <div className="absolute top-12 sm:top-16 bottom-12 sm:bottom-16 left-1/2 w-px bg-white/10 z-0" />
          <div className="absolute left-12 sm:left-16 right-12 sm:right-16 top-1/2 h-px bg-white/10 z-0" />

          {/* Labels */}
          <div className="absolute top-2 sm:top-8 left-1/2 -translate-x-1/2 text-xs sm:text-base font-mono text-primary font-bold uppercase tracking-wider bg-black/50 px-2 rounded backdrop-blur-sm">Hyper-Personalized</div>
          <div className="absolute bottom-2 sm:bottom-8 left-1/2 -translate-x-1/2 text-xs sm:text-base font-mono text-muted-foreground uppercase tracking-wider bg-black/50 px-2 rounded backdrop-blur-sm">Generic</div>
          <div className="absolute left-2 sm:left-8 top-1/2 -translate-y-1/2 -rotate-90 text-xs sm:text-base font-mono text-muted-foreground uppercase tracking-wider bg-black/50 px-2 rounded backdrop-blur-sm whitespace-nowrap">Manual</div>
          <div className="absolute right-2 sm:right-8 top-1/2 -translate-y-1/2 rotate-90 text-xs sm:text-base font-mono text-primary font-bold uppercase tracking-wider bg-black/50 px-2 rounded backdrop-blur-sm whitespace-nowrap">Autonomous</div>

          {/* Quadrant Highlights */}
          <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-primary/5 rounded-tr-xl sm:rounded-tr-3xl pointer-events-none" />
          <div className="hidden sm:block absolute top-12 sm:top-16 right-12 sm:right-16 text-primary/80 text-sm lg:text-lg font-mono font-bold uppercase tracking-widest">The Holy Grail</div>

          {/* Competitor Dots */}
          <div className="absolute bottom-[25%] left-[25%] flex flex-col items-center gap-1 sm:gap-2 -translate-x-1/2 -translate-y-1/2 group">
            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-muted-foreground border-2 border-background shadow-lg group-hover:scale-125 transition-transform" />
            <span className="text-xs sm:text-base text-muted-foreground font-bold bg-black/80 px-1 sm:px-2 py-0.5 sm:py-1 rounded">Banks</span>
          </div>
          <div className="absolute bottom-[35%] left-[35%] flex flex-col items-center gap-1 sm:gap-2 -translate-x-1/2 -translate-y-1/2 group">
            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-muted-foreground border-2 border-background shadow-lg group-hover:scale-125 transition-transform" />
            <span className="text-xs sm:text-base text-muted-foreground font-bold bg-black/80 px-1 sm:px-2 py-0.5 sm:py-1 rounded">Cred</span>
          </div>

          <div className="absolute top-[35%] left-[30%] flex flex-col items-center gap-1 sm:gap-2 -translate-x-1/2 -translate-y-1/2 group">
            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-muted-foreground border-2 border-background shadow-lg group-hover:scale-125 transition-transform" />
            <span className="text-xs sm:text-base text-muted-foreground font-bold bg-black/80 px-1 sm:px-2 py-0.5 sm:py-1 rounded">Jar</span>
          </div>

          <div className="absolute bottom-[30%] right-[30%] flex flex-col items-center gap-1 sm:gap-2 -translate-x-1/2 -translate-y-1/2 group">
            <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-muted-foreground border-2 border-background shadow-lg group-hover:scale-125 transition-transform" />
            <span className="text-xs sm:text-base text-muted-foreground font-bold bg-black/80 px-1 sm:px-2 py-0.5 sm:py-1 rounded">Zerodha</span>
          </div>

          {/* ZenCap - Top Right */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.5, type: "spring" }}
            className="absolute top-[25%] right-[25%] flex flex-col items-center gap-1 sm:gap-3 -translate-x-1/2 -translate-y-1/2 z-20"
          >
            <div className="relative">
              <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-primary border-2 sm:border-4 border-black shadow-[0_0_30px_rgba(0,255,0,0.6)] animate-pulse" />


            </div>
            <span className="text-sm sm:text-lg text-primary font-bold bg-black/90 px-2 sm:px-4 py-1 sm:py-1.5 rounded border border-primary/30 shadow-xl">ZenCap</span>
          </motion.div>

        </motion.div>

        {/* Right: The Moat (5 Cols on desktop) */}
        <motion.div
          initial={isPreview ? {} : { opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="lg:col-span-5 flex flex-col justify-center gap-4 sm:gap-8"
        >
          <div className="bg-gradient-to-br from-card to-card/50 border border-primary/20 rounded-xl sm:rounded-2xl lg:rounded-3xl p-4 sm:p-6 lg:p-10 relative overflow-hidden shadow-2xl">
            <div className="absolute top-0 left-0 w-1 sm:w-1.5 h-full bg-primary" />

            <div className="flex items-center gap-3 sm:gap-5 mb-4 sm:mb-8">
              <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 rounded-xl lg:rounded-2xl bg-primary/10 border border-primary/30 flex items-center justify-center text-primary shadow-[0_0_15px_rgba(0,255,0,0.1)]">
                <ArrowUpRight className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8" />
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white">The ZenCap Moat</h3>
                <p className="text-xs sm:text-sm text-muted-foreground font-medium">Why we win the top-right</p>
              </div>
            </div>

            <div className="space-y-4 sm:space-y-6 lg:space-y-8">
              {moatPoints.map((point, i) => (
                <div key={i} className="flex gap-3 sm:gap-5 group">
                  <div className="mt-1 shrink-0 w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:text-black transition-colors duration-300">
                    {React.cloneElement(point.icon, { className: "w-3 h-3 sm:w-4 sm:h-4 group-hover:text-black transition-colors duration-300" })}
                  </div>
                  <div>
                    <h4 className="font-bold text-white text-base sm:text-lg lg:text-xl mb-1 sm:mb-2 group-hover:text-primary transition-colors">{point.title}</h4>
                    <p className="text-xs sm:text-sm lg:text-base text-muted-foreground leading-relaxed">{point.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Slide10Roadmap;